import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'

import S from './Content.styled'
import * as Svg from '../../Svg'
import { useDropdownContext } from '../DropdownContext'
import { TabNavigation } from '../../TabNavigation'

const Content = ({ children, contentFooter }) => {
  const {
    isOpen,
    toggle,
    mobileHeading,
    alignContent,
    contentPosition,
    themeColor,
    dropdownContentId,
  } = useDropdownContext()

  const scrollableRef = useRef(null)
  const startMarkerRef = useRef(null)
  const endMarkerRef = useRef(null)

  const [isContentScrollable, setIsContentScrollable] = useState(false)
  const [isStartMarkerVisible, setIsStartMarkerVisible] = useState(true)
  const [isEndMarkerVisible, setIsEndMarkerVisible] = useState(true)

  const observerCallback = useCallback((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (target === startMarkerRef.current)
        setIsStartMarkerVisible(isIntersecting)
      if (target === endMarkerRef.current) setIsEndMarkerVisible(isIntersecting)
    })
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const observer = new IntersectionObserver(observerCallback, {
      root: scrollableRef.current,
      threshold: 0,
    })

    observer.observe(startMarkerRef.current)
    observer.observe(endMarkerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [isOpen, observerCallback])

  useEffect(() => {
    if (!isOpen) return

    setIsContentScrollable(
      scrollableRef.current.offsetHeight < scrollableRef.current.scrollHeight
    )
  }, [isOpen])

  const onCloseClick = () => {
    toggle()

    if (document.body.classList.contains('dropdown-mobile-menu-open')) {
      document.body.classList.remove('dropdown-mobile-menu-open')
    }
  }

  return (
    <>
      {isOpen && (
        <S.Content
          id={dropdownContentId}
          className="dropdown-menu-content"
          alignContent={alignContent}
          contentPosition={contentPosition}
          themeColor={themeColor}
          hidden={!isOpen}
        >
          <S.MobileHeading themeColor={themeColor}>
            <h1 className="heading">{mobileHeading}</h1>
            <TabNavigation
              onEnterPress={onCloseClick}
              onClick={onCloseClick}
              isIcon
              isOutline
            >
              <Svg.Cross className="icon-close" />
            </TabNavigation>
          </S.MobileHeading>
          <S.Scrollable
            ref={scrollableRef}
            isStartMarkerVisible={!isStartMarkerVisible}
            isEndMarkerVisible={!isEndMarkerVisible}
            themeColor={themeColor}
          >
            <div className="content">
              <div className="fade" />
              <S.Children
                className="children"
                themeColor={themeColor}
                role="menu"
              >
                <span
                  className="marker"
                  ref={startMarkerRef}
                  aria-hidden="true"
                />
                {children}
                <span
                  className="marker"
                  ref={endMarkerRef}
                  aria-hidden="true"
                />
              </S.Children>
            </div>
          </S.Scrollable>
          <S.Footer>{contentFooter && contentFooter}</S.Footer>
        </S.Content>
      )}
    </>
  )
}

Content.propTypes = {
  children: PropTypes.node.isRequired,
  contentFooter: PropTypes.node,
}

export default Content
